import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuDivider,
  MenuPopover,
  MenuTrigger,
  MenuItemRadio,
  makeStyles,
  tokens,
  shorthands,
  Label,
  Input,
  useId,
  mergeClasses,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
  ChevronRight20Filled,
  ChevronRight20Regular,
  CalendarLtr20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import {
  SimpleDateRangePicker,
} from './simple-date-range-picker';
import {
  CSMLTimeFrame,
} from '../../model';
import {
  civilDateRangeEqual,
} from '../../utils';


const ChevronRightIcon = bundleIcon(ChevronRight20Filled, ChevronRight20Regular);

const useStyles = makeStyles({
  menuPopover: {
    minWidth: '240px',
    ...shorthands.padding('4px'),
  },
  menuButton: {
    // This is a known fluent ui issue between Popover and MenuButton: https://github.com/microsoft/fluentui/issues/31804
    // This bug has been fixed in the latest version, but as we use legacy version, update the style as a workaround.
    backgroundColor: 'transparent',
    '&[aria-expanded="true"]': {
      backgroundColor: '#EBEBEB',
    },
  },
  unselectedMenuButton: {
    '&[aria-expanded="false"]': {
      fontWeight: 'unset',
    },
  },
  enabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForeground2BrandHover,
  },
  disabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForegroundDisabled,
  },
});

const useCustomItemStyles = makeStyles({
  menuPopover: {
    maxWidth: 'unset',
    '& .compared-date-range-picker-dialog': {
      top: 'auto !important',
      left: 'auto !important',
      right: 'auto !important',
      width: 'auto !important',
      height: 'auto !important',
      paddingLeft: '14px',
      paddingRight: '14px',
      paddingTop: '14px',
      position: 'relative',
      boxShadow: 'none',
      ...shorthands.border('0px', 'none'),
    },
  },
  item: {
    '& .fui-MenuItem__content': {
      display: 'flex',
      paddingRight: '0px',
    },
  },
  text: {
    flexGrow: '1',
  },
  icon: {
  },
  startDate: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '28px',
    marginRight: '28px',
    maxWidth: '160px',
  },
  endDate: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '28px',
    marginRight: '28px',
    marginBottom: '6px',
    maxWidth: '160px',
  },
  input: {
    paddingRight: '0px',
    height: '32px',
    lineHeight: '32px',
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  contentAfter: {
    paddingRight: '6px',
    paddingLeft: '8px',
    '& svg': {
      display: 'block',
    },
  },
  contentLabel: {
    marginBottom: '6px',
    marginTop: '8px',
  },
});

const CustomItem = (props) => {
  const {
    i18n,
    isCustomSelected,
    timeZone,
    customDateRange,
    setCustomDateRange,
    customRangeStartStr,
    customRangeEndStr,
    name,
    customOption,
  } = props;

  const classes = useCustomItemStyles();
  const menuItemRef = useRef(null);
  const startDateId = useId('startDate');
  const afterStartDateId = useId('afterStartDateId');
  const startDateRef = useRef(null);
  const endDateId = useId('endDate');
  const afterEndDateId = useId('afterEndDateId');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const isOpen = useMemo(() => isDatePickerOpen && isCustomSelected, [isDatePickerOpen, isCustomSelected]);
  const onOpenChange = (e, data) => {
    setIsDatePickerOpen(data.open);
  };

  // date range picker related handling
  // selecting 'start' or 'end' on date range picker
  const isSelectingStartDate = useRef(true);

  const onSelectedDateRangeChange = useCallback((newRange) => {
    setCustomDateRange(newRange);
  }, [setCustomDateRange]);

  const openDatePickerForStart = useCallback(() => {
    setIsDatePickerOpen(true);
    isSelectingStartDate.current = true;
  }, []);

  const openDatePickerForEnd = useCallback(() => {
    setIsDatePickerOpen(true);
    isSelectingStartDate.current = false;
  }, []);

  const onStartKeyDown = useCallback((event) => {
    if (event.code === 'Enter') {
      openDatePickerForStart();
      event.preventDefault();
    } else if (event.code === 'ArrowUp') {
      if (menuItemRef.current) {
        menuItemRef.current.focus();
        event.preventDefault();
      }
    }
  }, [openDatePickerForStart]);

  const onEndKeyDown = useCallback((event) => {
    if (event.code === 'Enter') {
      openDatePickerForEnd();
      event.preventDefault();
    } else if (event.code === 'ArrowUp') {
      if (startDateRef.current) {
        startDateRef.current.focus();
        event.preventDefault();
      }
    }
  }, [openDatePickerForEnd]);

  return (
    <>
      <Menu open={isOpen} onOpenChange={onOpenChange}>
        <MenuTrigger disableButtonEnhancement>
          <MenuItemRadio ref={menuItemRef} name={name} value={customOption.key} hasSubmenu className={classes.item}>
            <span className={classes.text}>
              {customOption.text}
            </span>
            <span className={classes.icon}>
              <ChevronRightIcon />
            </span>
          </MenuItemRadio>
        </MenuTrigger>

        <MenuPopover className={classes.menuPopover}>
          <MenuList>
            <SimpleDateRangePicker
              i18n={i18n}
              defaultSelectedDateRange={customDateRange}
              defaultIsSelectingStartDate={isSelectingStartDate.current}
              onDateRangeChange={onSelectedDateRangeChange}
              timeZone={timeZone}
            />
          </MenuList>
        </MenuPopover>
      </Menu>
      {isCustomSelected && (
        <div className="custom-date-range">
          <div className={classes.startDate}>
            <Label className={classes.contentLabel} htmlFor={startDateId}>{i18n.getString(_TL_('Start date'))}</Label>
            <Input
              className={mergeClasses(classes.input, 'start-date-input')}
              ref={startDateRef}
              onClick={() => openDatePickerForStart()}
              onKeyDown={onStartKeyDown}
              readOnly
              contentAfter={
                <div className={classes.contentAfter} id={afterStartDateId}>
                  <CalendarLtr20Regular />
                </div>
              }
              value={customRangeStartStr}
              aria-labelledby={`${startDateId} ${afterStartDateId}`}
              id={startDateId}
            />
          </div>
          <div className={classes.endDate}>
            <Label className={classes.contentLabel} htmlFor={endDateId}>{i18n.getString(_TL_('End date'))}</Label>
            <Input
              className={mergeClasses(classes.input, 'end-date-input')}
              onClick={() => openDatePickerForEnd()}
              onKeyDown={onEndKeyDown}
              readOnly
              contentAfter={
                <div className={classes.contentAfter} id={afterEndDateId}>
                  <CalendarLtr20Regular />
                </div>
              }
              value={customRangeEndStr}
              aria-labelledby={`${endDateId} ${afterEndDateId}`}
              id={endDateId}
            />
          </div>
        </div>
      )}
    </>
  );
};

export const DateAddedFilter = (props) => {
  const {
    i18n,
    dateFormat,
    timeZone,
    defaultTimeFrame,
    defaultCustomDateRange,
    options,
    onChange,
    forceClear,
    onDismiss,
    defaultOpen = false,
  } = props;

  const classes = useStyles();
  const name = 'date';
  const [checkedValues, setCheckedValues] = useState({
    [name]: [defaultTimeFrame],
  });
  const isCustomSelected = useMemo(() => checkedValues[name].includes(CSMLTimeFrame.Custom), [checkedValues]);
  const customOption = useMemo(() => options.find(option => option.key === CSMLTimeFrame.Custom), [options]);
  const optionsWithoutCustom = useMemo(() => options.filter(option => option.key !== CSMLTimeFrame.Custom), [options]);
  // date range user selected for 'Custom'
  const [customDateRange, setCustomDateRange] = useState(defaultCustomDateRange);

  // dropdown related handling
  const formatDate = useCallback(
    date => i18n.formatCivilDate(date, { raw: dateFormat, timeZone }),
    [dateFormat, i18n, timeZone]
  );

  const customRangeStartStr = useMemo(
    () => formatDate(customDateRange.start),
    [formatDate, customDateRange.start]
  );

  const customRangeEndStr = useMemo(
    () => formatDate(customDateRange.end),
    [formatDate, customDateRange.end]
  );

  const selectedChoice = useMemo(
    () => options.find(r => r.key === checkedValues[name][0]),
    [checkedValues, options]
  );

  // the string shown on dropdown title
  const dropdownTitle = useMemo(() => {
    if (!selectedChoice) {
      return null;
    }
    if (selectedChoice.key === CSMLTimeFrame.Custom) {
      return `${customRangeStartStr} - ${customRangeEndStr}`;
    }
    return selectedChoice.text;
  }, [customRangeEndStr, customRangeStartStr, selectedChoice]);

  // when the X button on dropdown title is clicked
  const onDropdownClear = useCallback(() => {
    if (onChange && defaultTimeFrame !== CSMLTimeFrame.Anytime) {
      onChange({ timeframe: CSMLTimeFrame.Anytime, customDateRange });
    }
    if (onDismiss) {
      onDismiss();
    }
  }, [customDateRange, defaultTimeFrame, onChange, onDismiss]);

  // when dropdown list is dismissed
  const onDropdownDismiss = useCallback(() => {
    const timeframe = checkedValues[name][0];
    if (timeframe && onChange) {
      if (defaultTimeFrame !== timeframe
        || !civilDateRangeEqual(defaultCustomDateRange, customDateRange)) {
        onChange({ timeframe, customDateRange }, dropdownTitle);
      }
    }
  }, [checkedValues, customDateRange, defaultCustomDateRange, defaultTimeFrame, dropdownTitle, onChange]);

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const clear = useCallback(() => {
    setIsOpen(false);
    setCheckedValues(() => ({
      [name]: [],
    }));
    onDropdownClear();
  }, [onDropdownClear]);

  const onCheckedValueChange = (e, data) => {
    const {
      checkedItems,
    } = data;
    setCheckedValues(s => ({
      ...s,
      [name]: checkedItems,
    }));
    if (checkedItems && checkedItems.length > 0 && checkedItems[0] !== CSMLTimeFrame.Custom) {
      setIsOpen(false);
    }
  };

  const isSelected = useMemo(() => checkedValues[name].length > 0 && checkedValues[name][0] !== CSMLTimeFrame.Anytime, [checkedValues]);

  const onOpenChange = (e, data) => {
    setIsOpen(data.open);
  };

  useEffect(() => {
    if (forceClear) {
      clear();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClear]);

  useEffect(() => {
    if (!isOpen) {
      onDropdownDismiss();
    }
  }, [isOpen, onDropdownDismiss]);

  return (
    <Popover trapFocus open={isOpen} onOpenChange={onOpenChange} positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <MenuButton appearance="subtle" className={isSelected ? mergeClasses(classes.menuButton, 'date-filter-button') : mergeClasses(classes.menuButton, classes.unselectedMenuButton, 'date-filter-button')}>
          {isSelected ? dropdownTitle : i18n.getString(_TL_('Date added'))}
        </MenuButton>
      </PopoverTrigger>

      <PopoverSurface className={mergeClasses(classes.menuPopover, 'date-filter-menu')}>
        <MenuList checkedValues={checkedValues} onCheckedValueChange={onCheckedValueChange} hasCheckmarks>
          {optionsWithoutCustom.map(option => (
            <MenuItemRadio key={option.key} name={name} value={option.key}>
              {option.text}
            </MenuItemRadio>
          ))}
          {customOption &&
            <CustomItem
              i18n={i18n}
              checkedValues={checkedValues}
              isCustomSelected={isCustomSelected}
              customDateRange={customDateRange}
              timeZone={timeZone}
              setCustomDateRange={setCustomDateRange}
              customOption={customOption}
              customRangeStartStr={customRangeStartStr}
              customRangeEndStr={customRangeEndStr}
              name={name}
            />}
          <MenuDivider />
        </MenuList>
        <div>
          <Button
            className={isSelected ? classes.enabledClearButton : classes.disabledClearButton}
            disabled={!isSelected}
            appearance="transparent"
            icon={<Dismiss20Regular />}
            onClick={() => clear()}
          >
            {i18n.getString(_TL_('Clear'))}
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
};

